import {
    MutationCache,
    QueryCache,
    QueryClient,
    defaultShouldDehydrateQuery,
    isServer,
    type Mutation,
} from '@tanstack/react-query';

import { getErrorNotification, showErrorNotification } from 'lib/api/errors/errorNotification';
import type { ServerError } from 'models';
import { isLowMemoryDevice } from 'utils/device';

// All ISR pages revalidate at ~120s, so use that as the default SSR staleTime on both
// server and client — keeps queries fresh enough to avoid an immediate client refetch
// after hydration without over-caching.
const DEFAULT_STALE_TIME_MS = 120 * 1000;
const ERROR_TOAST_DEDUPE_MS = 30 * 1000;

// Client-side inactive-query retention. Shortened on <=4 GB devices so unused query
// data is reclaimed sooner, trading a few extra refetches for a smaller heap.
const CLIENT_GC_TIME_MS = isLowMemoryDevice() ? 2 * 60 * 1000 : 5 * 60 * 1000;

const lastErrorToastBySource = new Map<string, { key: string; shownAt: number }>();

const isServerError = (error: unknown): error is ServerError => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'unauthorized' in error &&
        'forbidden' in error &&
        'invalid' in error &&
        'conflict' in error
    );
};

const shouldShowGlobalErrorToast = (meta: Record<string, unknown> | undefined, defaultValue: boolean): boolean => {
    return typeof meta?.globalErrorToast === 'boolean' ? meta.globalErrorToast : defaultValue;
};

type MutationToastSource = Pick<Mutation<unknown, unknown, unknown, unknown>, 'mutationId'>;

const getMutationToastSourceKey = (mutation: MutationToastSource): string => {
    return `mutation:${mutation.mutationId}`;
};

const maybeShowServerErrorToast = (
    error: unknown,
    sourceKey: string,
    meta: Record<string, unknown> | undefined,
    defaultGlobalErrorToast: boolean,
): void => {
    if (isServer || !shouldShowGlobalErrorToast(meta, defaultGlobalErrorToast)) return;

    const notification = getErrorNotification(error);
    if (notification?.type !== 'server') return;

    const lastToast = lastErrorToastBySource.get(sourceKey);
    const now = Date.now();
    if (lastToast?.key === notification.key && now - lastToast.shownAt < ERROR_TOAST_DEDUPE_MS) return;

    showErrorNotification(notification);
    lastErrorToastBySource.set(sourceKey, { key: notification.key, shownAt: now });
};

function makeQueryClient() {
    return new QueryClient({
        queryCache: new QueryCache({
            onError: (error, query) => {
                maybeShowServerErrorToast(error, `query:${query.queryHash}`, query.meta, false);
            },
            onSuccess: (_data, query) => {
                lastErrorToastBySource.delete(`query:${query.queryHash}`);
            },
        }),
        mutationCache: new MutationCache({
            onError: (error, _variables, _context, mutation) => {
                maybeShowServerErrorToast(error, getMutationToastSourceKey(mutation), mutation.meta, true);
            },
            onSuccess: (_data, _variables, _context, mutation) => {
                lastErrorToastBySource.delete(getMutationToastSourceKey(mutation));
            },
        }),
        defaultOptions: {
            queries: {
                // With SSR, we set a default staleTime above 0 to avoid refetching
                // immediately on the client after hydration. All ISR pages revalidate
                // at ~120 seconds, so match that.
                staleTime: DEFAULT_STALE_TIME_MS,
                // Server-side gcTime defaults to Infinity for automatic cleanup
                // Client-side uses the specified gcTime (shortened on low-memory devices)
                gcTime: isServer ? Infinity : CLIENT_GC_TIME_MS,
                retry: (failureCount, error: unknown) => {
                    if (isServerError(error)) {
                        if (error?.unauthorized || error?.forbidden || error?.invalid || error?.conflict) {
                            return false;
                        }
                    }

                    if (failureCount >= 2) {
                        return false;
                    }

                    return true;
                },
                refetchOnWindowFocus: false,
            },
            mutations: {
                retry: 0,
            },
            dehydrate: {
                // Include pending queries in dehydration for streaming
                shouldDehydrateQuery: (query) => defaultShouldDehydrateQuery(query) || query.state.status === 'pending',
            },
        },
    });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
    if (isServer) {
        // Server: always make a new query client
        return makeQueryClient();
    } else {
        // Browser: make a new query client if we don't already have one
        // This is very important, so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        if (!browserQueryClient) browserQueryClient = makeQueryClient();
        return browserQueryClient;
    }
}
